import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TopicSelect } from "@/components/filters/TopicSelect";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";
import NoteForm from "./NoteForm";

const sb = supabase as unknown as SupabaseClient<any>;

type Topic = { id: string; name: string };

type Note = { id: string; title: string; content: string; estimated: number; topic_id: string | null };

const fetchTopics = async (): Promise<Topic[]> => {
  const { data, error } = await sb.from("topics").select("id,name").order("name", { ascending: true });
  if (error) throw error;
  return data ?? [];
};

const fetchNotes = async (): Promise<Note[]> => {
  const { data, error } = await sb
    .from("notes")
    .select("id,title,content,estimated,topic_id")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
};

const NoteManager = () => {
  const qc = useQueryClient();
  const { data: topics = [] } = useQuery({ queryKey: ["topics"], queryFn: fetchTopics });
  const { data: notes = [], isLoading, error } = useQuery({ queryKey: ["notes"], queryFn: fetchNotes });

  const byId = useMemo(() => new Map(topics.map((t) => [t.id, t])), [topics]);

  const [editing, setEditing] = useState<Note | null>(null);
  const [query, setQuery] = useState("");
  const [topicFilter, setTopicFilter] = useState<string | undefined>();
  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return notes.filter((n) => {
      const matchesQuery = n.title.toLowerCase().includes(q) || n.content.toLowerCase().includes(q);
      const matchesTopic = !topicFilter || n.topic_id === topicFilter;
      return matchesQuery && matchesTopic;
    });
  }, [notes, query, topicFilter]);

  type ExamLabel = { id: string; slug: string; name: string };
  const { data: labels = [] } = useQuery({
    queryKey: ["exam-labels"],
    queryFn: async (): Promise<ExamLabel[]> => {
      const { data, error } = await sb.from("exam_labels").select("id,slug,name").order("name", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const [createLabelIds, setCreateLabelIds] = useState<string[]>([]);
  const [editLabelIds, setEditLabelIds] = useState<string[]>([]);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      const { data, error } = await sb.from("note_exam_labels").select("label_id").eq("note_id", editing.id);
      if (!error) setEditLabelIds((data || []).map((r: any) => r.label_id as string));
    })();
  }, [editing]);

  const createMutation = useMutation({
    mutationFn: async (payload: { title: string; content: string; topic_id: string | null; estimated: number }) => {
      const { data, error } = await sb.from("notes").insert(payload).select("id").maybeSingle();
      if (error) throw error;
      const noteId = data?.id as string | undefined;
      if (noteId && createLabelIds.length) {
        const rows = createLabelIds.map((label_id) => ({ note_id: noteId, label_id }));
        const { error: mapErr } = await sb.from("note_exam_labels").insert(rows);
        if (mapErr) throw mapErr;
      }
    },
  onSuccess: async () => {
    toast({ title: "Note created" });
    setCreateLabelIds([]);
    await qc.invalidateQueries({ queryKey: ["notes"] });
  },
  onError: (e: any) =>
    toast({ title: "Failed to create note", description: e?.message ?? "An error occurred", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; title: string; content: string; topic_id: string | null; estimated: number }) => {
      const { error } = await sb.from("notes").update(payload).eq("id", id);
      if (error) throw error;
      // Sync labels for this note
      const { error: delErr } = await sb.from("note_exam_labels").delete().eq("note_id", id);
      if (delErr) throw delErr;
      if (editLabelIds.length) {
        const rows = editLabelIds.map((label_id) => ({ note_id: id, label_id }));
        const { error: insErr } = await sb.from("note_exam_labels").insert(rows);
        if (insErr) throw insErr;
      }
    },
  onSuccess: async () => {
    toast({ title: "Note updated" });
    setEditing(null);
    await qc.invalidateQueries({ queryKey: ["notes"] });
  },
  onError: (e: any) =>
    toast({ title: "Failed to update note", description: e?.message ?? "An error occurred", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await sb.from("notes").delete().eq("id", id);
      if (error) throw error;
    },
  onSuccess: async () => {
    toast({ title: "Note deleted" });
    await qc.invalidateQueries({ queryKey: ["notes"] });
  },
  onError: (e: any) =>
    toast({ title: "Failed to delete note", description: e?.message ?? "An error occurred", variant: "destructive" }),
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <Card>
        <CardHeader>
          <CardTitle>Add new note</CardTitle>
        </CardHeader>
        <CardContent>
          <NoteForm
            topics={topics}
            submitting={createMutation.isPending}
            examLabels={labels}
            examLabelIds={createLabelIds}
            onLabelsChange={setCreateLabelIds}
            onSubmit={(payload) => createMutation.mutate(payload)}
            submitLabel="Create"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>Existing notes</CardTitle>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <div className="w-full sm:w-72">
              <Input placeholder="Search notes" value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
            <TopicSelect topics={topics} value={topicFilter} onChange={setTopicFilter} />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="text-destructive">Failed to load notes.</div>
          ) : notes.length === 0 ? (
            <div className="text-muted-foreground">No notes yet.</div>
          ) : filtered.length === 0 ? (
            <div className="text-muted-foreground">No matching notes.</div>
          ) : (
            <div className="relative w-full overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[35%] sticky top-0 bg-background z-10">Title</TableHead>
                    <TableHead className="w-[25%] sticky top-0 bg-background z-10">Topic</TableHead>
                    <TableHead className="w-[15%] sticky top-0 bg-background z-10">Est.</TableHead>
                    <TableHead className="w-[25%] sticky top-0 bg-background z-10 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((n) => (
                    <TableRow key={n.id}>
                      <TableCell className="truncate">{n.title}</TableCell>
                      <TableCell className="text-muted-foreground">{n.topic_id ? byId.get(n.topic_id)?.name ?? "—" : "—"}</TableCell>
                      <TableCell>~{n.estimated}m</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="hover-scale">Actions</Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="z-50 bg-popover">
                            <DropdownMenuItem onClick={() => setEditing(n)}>Edit</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <DropdownMenuItem className="text-destructive focus:text-destructive">Delete</DropdownMenuItem>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete note?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteMutation.mutate(n.id)} disabled={deleteMutation.isPending}>
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit note</DialogTitle>
          </DialogHeader>
          {editing && (
            <NoteForm
              topics={topics}
              initial={editing}
              submitting={updateMutation.isPending}
              examLabels={labels}
              examLabelIds={editLabelIds}
              onLabelsChange={setEditLabelIds}
              onSubmit={(payload) => updateMutation.mutate({ id: editing.id, ...payload })}
              onCancel={() => setEditing(null)}
              submitLabel="Update"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NoteManager;
